import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { buyerSignupSchema, type BuyerSignupData } from "@/lib/validationSchemas"
import {
  Form,
  FormControl,
  FormErrorMessage,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form"
import { FormSubmitButton } from "@/components/ui/form-submit-button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useNavigate, Link } from "react-router-dom"
import { toastSuccess, toastError } from "@/lib/toast"
import { ArrowLeft, MailCheck } from "lucide-react"
import { BrandLogo } from "@/components/ui/brand-logo"
import { mexicanStates } from "@/utils/mexicanStates"
import { useAuth, UserType } from "@/hooks/useAuth"
import { FormInputField } from "@/components/ui/form-input-field"
import { useLanguage } from "@/hooks/useLanguage"

const BuyerSignup = () => {
  const { t, language } = useLanguage()
  const [loading, setLoading] = useState(false)
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null)
  const navigate = useNavigate()
  const { register } = useAuth()

  const text = t.buyerSignup

  const form = useForm<BuyerSignupData>({
    resolver: zodResolver(buyerSignupSchema),
    defaultValues: {
      email: "",
      password: "",
      name: "",
      company: "",
      phone: "",
      location: "",
      industry: "",
    },
  })

  const onSubmit = async (data: BuyerSignupData) => {
    setLoading(true)

    try {
      const response = await register({
        email: data.email,
        password: data.password,
        displayName: data.name,
        userType: UserType.Buyer,
      })

      toastSuccess(text.signupSuccess)
      setVerificationEmail(response.email)
      form.reset()
    } catch (error: any) {
      console.error("Signup error:", error)
      let errorMessage = text.signupError

      if (Array.isArray(error)) {
        errorMessage = error.map((err) => err.description).join(" ")
      } else if (error.message) {
        errorMessage = error.message
      }

      toastError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const industries = [
    { value: "restaurant", label: text.industries.restaurant },
    { value: "petfood", label: text.industries.petfood },
    { value: "processing", label: text.industries.processing },
    { value: "retail", label: text.industries.retail },
    { value: "wholesale", label: text.industries.wholesale },
    { value: "other", label: text.industries.other },
  ]

  return (
    <div className="flex min-h-screen bg-background text-foreground transition-colors duration-300">
      {/* Left Panel - Hero Image */}
      <div className="relative hidden w-1/2 overflow-hidden bg-black lg:flex">
        <img
          src="/assets/buyer-signup-hero.png"
          alt="Premium Meat"
          className="absolute inset-0 h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

        <div className="absolute left-8 top-8 z-20 flex items-center space-x-3 cursor-pointer" onClick={() => navigate("/")}>
          <div className="flex h-10 w-10 items-center justify-center bg-white rounded-sm">
            <BrandLogo className="h-6 w-6 text-black" />
          </div>
          <span className="font-sans text-xl font-bold tracking-tight text-white">{text.brandName}</span>
        </div>

        <div className="relative z-10 flex h-full w-full flex-col justify-end p-16 pb-24 text-white">
          <h1 className="mb-6 max-w-lg text-[3.5rem] font-bold leading-[1.1] tracking-tight whitespace-pre-line">
            {text.heroTitle}
          </h1>
          <p className="max-w-md text-lg text-white/80 leading-relaxed">
            {text.heroSubtitle}
          </p>

          <div className="mt-16 flex gap-2">
            <div className="h-1 w-12 bg-white/30 rounded-full" />
            <div className="h-1 w-8 bg-[#A87B51] rounded-full" />
          </div>
        </div>
      </div>

      {/* Right Panel - Form Area */}
      <main className="flex flex-1 flex-col justify-center px-6 py-12 sm:px-12 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-[500px]">
          <div className="mb-10 lg:hidden flex items-center mb-12">
            <div className="flex h-10 w-10 items-center justify-center bg-primary rounded-sm mr-3">
              <BrandLogo className="h-6 w-6 text-white" />
            </div>
            <span className="font-sans text-xl font-bold tracking-tight">{text.brandName}</span>
          </div>
          <div className="my-12 w-full max-w-md">
            <div className="mb-8 text-center">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="mb-4 p-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {text.backHome}
              </Button>
            </div>
          </div>
          <div className="mb-10 text-left">
            <h2 className="mb-3 font-sans text-4xl font-extrabold tracking-tight text-slate-800 dark:text-foreground">
              {text.createAccount}
            </h2>
            <p className="text-muted-foreground leading-relaxed text-[15px]">
              {text.formSubtitle}
            </p>
          </div>

          {verificationEmail ? (
            <div className="space-y-8 border border-primary/20 bg-primary/5 p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-primary text-primary-foreground">
                <MailCheck className="h-6 w-6" />
              </div>
              <div className="space-y-3">
                <h3 className="font-display text-3xl uppercase leading-none tracking-wide text-foreground">
                  {text.checkEmailTitle}
                </h3>
                <p className="text-sm leading-6 text-muted-foreground">
                  {text.checkEmailDescription.replace("{email}", verificationEmail)}
                </p>
              </div>
              <Button
                type="button"
                onClick={() => navigate("/login")}
                className="h-12 rounded-sm bg-[#2A3F32] px-6 font-bold text-white hover:bg-[#1f2f25]"
              >
                {text.goToLogin}
              </Button>
            </div>
          ) : (
            <Form {...form}>
              <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{text.fullName}</FormLabel>
                      <FormControl>
                        <Input placeholder="John Doe" className="bg-white dark:bg-card h-12 shadow-sm border-border/60 focus-visible:ring-primary/20" {...field} />
                      </FormControl>
                      <FormErrorMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{text.companyName}</FormLabel>
                      <FormControl>
                        <Input placeholder="Prime Meats LLC" className="bg-white dark:bg-card h-12 shadow-sm border-border/60 focus-visible:ring-primary/20" {...field} />
                      </FormControl>
                      <FormErrorMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{text.phoneNumber} (Optional)</FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-white dark:bg-card h-12 shadow-sm border-border/60 focus-visible:ring-primary/20" {...field} />
                      </FormControl>
                      <FormErrorMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{text.location}</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger className="bg-white dark:bg-card h-12 shadow-sm border-border/60 focus:ring-primary/20">
                            <SelectValue placeholder={text.selectState} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-60 bg-white dark:bg-card">
                          {mexicanStates.map((state) => (
                            <SelectItem key={state.value} value={state.value}>
                              {state.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormErrorMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="industry"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{text.businessType}</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="bg-white dark:bg-card h-12 shadow-sm border-border/60 focus:ring-primary/20">
                          <SelectValue placeholder={text.selectIndustry} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent className="bg-white dark:bg-card">
                        {industries.map((industry) => (
                          <SelectItem key={industry.value} value={industry.value}>
                            {industry.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormErrorMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{text.email}</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="corporate@email.com" className="bg-white dark:bg-card h-12 shadow-sm border-border/60 focus-visible:ring-primary/20" {...field} />
                    </FormControl>
                    <FormErrorMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{text.password}</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" className="bg-white dark:bg-card h-12 shadow-sm border-border/60 focus-visible:ring-primary/20" {...field} />
                    </FormControl>
                    <FormErrorMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={loading}
                className="group mt-8 h-14 w-full bg-[#2A3F32] hover:bg-[#1f2f25] text-white shadow-md transition-all duration-300 rounded-md"
              >
                <span className="flex items-center justify-center font-bold tracking-wide">
                  {loading ? text.createButton : text.createButton}
                  {!loading && <span className="ml-2 transition-transform group-hover:translate-x-1">→</span>}
                </span>
              </Button>
              </form>
            </Form>
          )}

          <div className="mt-8 text-center text-[13px]">
            <span className="text-muted-foreground tracking-wide">{t.auth.alreadyHaveAccount} </span>
            <Link to="/login" className="font-bold text-[#A87B51] hover:underline hover:text-[#8a633f] transition-colors">
              {text.signIn}
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}

export default BuyerSignup

